import { useFrame, useLoader } from '@react-three/fiber'
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { DoubleSide, RepeatWrapping, TextureLoader } from 'three'

type MapStyle = 'street' | 'satellite'

interface MapGroundPlaneProps {
  lat: number
  long: number
  metersPerUnit?: number
  yPosition?: number
  scale?: number
  rotationY?: number
  xOffset?: number
  zOffset?: number
  mapStyle?: MapStyle
  onLoad?: () => void
  onLoadingChange?: (loading: boolean) => void
}

interface MapLayerProps extends MapGroundPlaneProps {
  targetOpacity: number
  onFadeComplete?: () => void
}

/**
 * Single map layer with fade animation support
 */
const MapLayer: FC<MapLayerProps> = ({
  lat,
  long,
  metersPerUnit = 1,
  yPosition = -30,
  scale = 0.64,
  rotationY = 180,
  xOffset = 0,
  zOffset = 0,
  mapStyle = 'street',
  targetOpacity,
  onFadeComplete,
  onLoad,
}) => {
  const mapService =
    mapStyle === 'satellite' ? 'World_Imagery' : 'World_Street_Map'
  const baseOpacity = mapStyle === 'satellite' ? 0.7 : 1

  const imageSize = 2048
  const mapUrl = useMemo(
    () =>
      `https://server.arcgisonline.com/ArcGIS/rest/services/${mapService}/MapServer/export?` +
      `bbox=${long - 0.003},${lat - 0.003},${long + 0.003},${lat + 0.003}` +
      `&size=${imageSize},${imageSize}` +
      `&format=png` +
      `&f=image` +
      `&bboxSR=4326&imageSR=4326`,
    [lat, long, mapService]
  )

  const texture = useLoader(TextureLoader, mapUrl)
  const opacityRef = useRef(0)
  const [currentOpacity, setCurrentOpacity] = useState(0)

  useEffect(() => {
    if (texture && onLoad) {
      onLoad()
    }
  }, [texture, onLoad])

  // Flip texture horizontally
  texture.wrapS = RepeatWrapping
  texture.repeat.x = -1
  texture.offset.x = 1

  // Animate opacity
  useFrame((_, delta) => {
    const target = targetOpacity * baseOpacity
    const current = opacityRef.current
    const diff = target - current

    if (Math.abs(diff) > 0.01) {
      const newOpacity = current + diff * Math.min(delta * 4, 1)
      opacityRef.current = newOpacity
      setCurrentOpacity(newOpacity)
    } else if (current !== target) {
      opacityRef.current = target
      setCurrentOpacity(target)
      if (targetOpacity === 0 && onFadeComplete) {
        onFadeComplete()
      }
    }
  })

  const degreesSpan = 0.005
  const metersPerDegree = 111320 * Math.cos((lat * Math.PI) / 180)
  const realMeters = degreesSpan * 2 * metersPerDegree
  const size = (realMeters / metersPerUnit) * scale
  const rotationYRad = (rotationY * Math.PI) / 180

  if (currentOpacity <= 0.01 && targetOpacity === 0) return null

  return (
    <group rotation={[0, rotationYRad, 0]}>
      <mesh
        position={[xOffset, yPosition, zOffset]}
        rotation={[-Math.PI / 2, Math.PI, 0]}
        renderOrder={-1}
        receiveShadow>
        <planeGeometry args={[size, size]} />
        <meshBasicMaterial
          map={texture}
          side={DoubleSide}
          transparent
          opacity={currentOpacity}
          depthWrite={false}
        />
      </mesh>
    </group>
  )
}

interface MapLayerState {
  style: MapStyle
  status: 'loading' | 'active' | 'fading-out'
}

/**
 * Wrapper that handles loading state and smooth transitions between map styles
 */
export const MapGroundPlaneWithFallback: FC<MapGroundPlaneProps> = (props) => {
  const [layers, setLayers] = useState<MapLayerState[]>([
    { style: props.mapStyle || 'street', status: 'loading' },
  ])
  const prevStyleRef = useRef(props.mapStyle)

  // Handle map style changes
  useEffect(() => {
    if (props.mapStyle && props.mapStyle !== prevStyleRef.current) {
      prevStyleRef.current = props.mapStyle

      setLayers((prev) => {
        // Mark existing active layers as fading out
        const updated = prev.map((layer) =>
          layer.status === 'active'
            ? { ...layer, status: 'fading-out' as const }
            : layer
        )
        // Add new loading layer
        return [
          ...updated,
          { style: props.mapStyle!, status: 'loading' as const },
        ]
      })
    }
  }, [props.mapStyle])

  // Notify parent of loading state changes
  const isLoading = layers.some((l) => l.status === 'loading')
  const { onLoadingChange } = props
  useEffect(() => {
    onLoadingChange?.(isLoading)
  }, [isLoading, onLoadingChange])

  const handleLayerLoaded = (style: MapStyle) => {
    setLayers((prev) =>
      prev.map((layer) =>
        layer.style === style && layer.status === 'loading'
          ? { ...layer, status: 'active' }
          : layer
      )
    )
  }

  const handleFadeComplete = (style: MapStyle) => {
    setLayers((prev) =>
      prev.filter(
        (layer) => layer.style !== style || layer.status !== 'fading-out'
      )
    )
  }

  return (
    <>
      {/* Loading placeholder */}
      {isLoading && <MapGroundPlanePlaceholder {...props} />}

      {/* Map layers with suspense */}
      {layers.map((layer) => (
        <React.Suspense key={`${layer.style}-${layer.status}`} fallback={null}>
          <MapLayer
            {...props}
            mapStyle={layer.style}
            targetOpacity={layer.status === 'fading-out' ? 0 : 1}
            onLoad={() => handleLayerLoaded(layer.style)}
            onFadeComplete={() => handleFadeComplete(layer.style)}
          />
        </React.Suspense>
      ))}
    </>
  )
}

/**
 * Infinite grid placeholder while map loads
 */
const MapGroundPlanePlaceholder: FC<MapGroundPlaneProps> = ({
  yPosition = -30,
}) => {
  return (
    <gridHelper
      args={[1000, 100, '#3a4a3a', '#2a3a2a']}
      position={[0, yPosition, 0]}
      renderOrder={-2}
    />
  )
}

/** @deprecated Use MapGroundPlaneWithFallback instead */
export const MapGroundPlane = MapLayer
